import { createApi } from '@reduxjs/toolkit/query/react';
import { backend_server_address } from '../../../../../backendServer';
import { axiosBaseQuery } from '../../../../../store/utility';

/**
 * Custom spectral bands API (`/api/model/series/spectral/bands/`).
 *
 * A created band appears as another `group` of its family chart, and the
 * backend backfills its history on the next extraction sweep; the preview
 * endpoint computes one week on the fly and persists nothing.
 */
export const spectralBandsAPI = createApi({
    reducerPath: 'spectralBandsAPI',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/model/series/spectral/' }),
    // No query of this slice provides the tag yet; the coming band view/delete
    // endpoints will, and mutations already invalidate it so they stay in sync.
    tagTypes: ['SpectralBand'],
    endpoints: (builder) => ({
        createBand: builder.mutation({
            // { node_id, family, fmin_hz, fmax_hz, name? } -> 201 with the band's group
            // identity, as its chart series carry it: {key, name, feature,
            // band: {definition fields}, family_feature}. The band surfaces through
            // the node metadata, whose cache the caller refreshes — this slice holds
            // no cached band state of its own.
            query: (data) => ({
                url: 'bands/',
                method: 'POST',
                withCredentials: true,
                data,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'SpectralBand', id: arg.node_id }],
        }),
        previewBand: builder.mutation({
            // { node_id, family, fmin_hz, fmax_hz, days?, axis? ("x"|"y"|"z", default "x") }
            // -> { dates, values } in the apiv3 typed-array encoding (dates: ns-epoch Int64Array)
            query: (data) => ({
                url: 'bands/preview/',
                method: 'POST',
                withCredentials: true,
                data,
            }),
        }),
    }),
});

export const { useCreateBandMutation, usePreviewBandMutation } = spectralBandsAPI;
export const spectralBandsAPIReducer = spectralBandsAPI.reducer;
export const spectralBandsAPIReducerPath = spectralBandsAPI.reducerPath;
export const spectralBandsAPIReducerMiddleware = spectralBandsAPI.middleware;
